'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

export type UseMediaQueryOptions = {
  /**
   * Value returned during SSR and on the first client render before `matchMedia` is available.
   * @default false
   */
  defaultMatches?: boolean;
  /**
   * If true, reads `matchMedia` synchronously on the initial render. Use only for client-only
   * components — it will cause a hydration mismatch in SSR'd trees whose initial state differs
   * from `defaultMatches`.
   * @default false
   */
  noSsr?: boolean;
};

/**
 * Subscribe to a CSS media query and re-render when its match state changes.
 *
 * Implementation mirrors MUI's `@mui/system/useMediaQuery` and Base UI's
 * `unstable-use-media-query`: built on `useSyncExternalStore`, with a
 * single memoized subscription per `query`.
 */
export function useMediaQuery(query: string, options: UseMediaQueryOptions = {}): boolean {
  const { defaultMatches = false, noSsr = false } = options;

  // `@media (...)` is the CSS form. matchMedia accepts both, but stripping keeps the cache key
  // consistent.
  const normalizedQuery = query.replace(/^@media( ?)/m, '');

  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';

  const getServerSnapshot = useCallback(() => defaultMatches, [defaultMatches]);

  const [getSnapshot, subscribe] = useMemo(() => {
    if (!supportsMatchMedia) {
      return [getServerSnapshot, () => () => {}];
    }
    const mql = window.matchMedia(normalizedQuery);
    return [
      () => mql.matches,
      (notify: () => void) => {
        mql.addEventListener('change', notify);
        return () => mql.removeEventListener('change', notify);
      },
    ];
  }, [normalizedQuery, supportsMatchMedia, getServerSnapshot]);

  // When noSsr is true we want the same snapshot for SSR and first-client paint so React's
  // useSyncExternalStore returns the live value immediately instead of `defaultMatches`.
  const serverSnapshot = noSsr && supportsMatchMedia ? getSnapshot : getServerSnapshot;

  return useSyncExternalStore(subscribe, getSnapshot, serverSnapshot);
}
